import { useCallback } from 'react';
import * as yup from 'yup';
import { useStringsContext } from '../../../../contexts/strings';
import Stack from '@mui/material/Stack';
import SelectWithValidate from '../../../common/input/select-with-validate';
import { SxStyle } from '../../../../types';
import svStrings from '../../../../sv-strings';
import { QuizPoll, QuizQuestion, QuizQuestionType } from '../types';
import { Option } from '../../../../types';
import TextInputWithValidate, {
  InputWithValidateProps,
} from '../../../common/input/text-input-with-validate';
import InputAdornment from '@mui/material/InputAdornment';
import Box from '@mui/material/Box';

const styles: SxStyle = {
  select: {
    flexGrow: 1,
  },
  input: {
    width: '150px',
  },
};

const quizQuestionOptions: QuizQuestionType[] = ['text', 'player'];
const quizQuestionOptionsText = [
  svStrings.polls.quizQuestionTextType,
  svStrings.polls.quizQuestionPlayerType,
];

type QuizQuestionsKey = keyof Pick<QuizPoll, 'questions'>;
type QuestionTypeKey = keyof Pick<QuizQuestion, 'type'>;
type QuestionTextKeys = keyof Pick<QuizQuestion, 'text' | 'timelimit'>;

const quizQuestionsKey: QuizQuestionsKey = 'questions';
const questionTypeKey: QuestionTypeKey = 'type';
const questionTextKey: QuestionTextKeys = 'text';
const questionTimelimitKey: QuestionTextKeys = 'timelimit';

const questionTitleValidation = yup.string().required(svStrings.validations.isRequired);
const questionTypeValidation = yup.string().required(svStrings.validations.isRequired);
const questionTimelimitValidation = yup
  .number()
  .integer(svStrings.validations.mustBeInteger)
  .min(0, svStrings.validations.mustBePositive)
  .typeError(svStrings.validations.isRequired);

interface Props extends Omit<InputWithValidateProps, 'onValue'> {
  selectedQuestion: QuizQuestion;
  onValue: (key: QuizQuestionsKey, value: QuizQuestion) => void;
  onChangeType: (type: QuizQuestionType) => void;
}

const PollQuizQuestion = ({
  selectedQuestion,
  onValue,
  onValidation,
  onChangeType,
  hideValidationError,
}: Props) => {
  const { strings } = useStringsContext();

  const handleTypeValue = useCallback(
    (key: QuestionTypeKey, value: Option | null, question: QuizQuestion) => {
      onChangeType(value as QuizQuestionType);
    },
    [onChangeType]
  );

  const handleTextValue = useCallback(
    (key: QuestionTextKeys, value: string | null, question: QuizQuestion) => {
      if (key === questionTimelimitKey) {
        question[key] = Number(value);
      } else {
        question[key] = value!;
      }
      onValue(quizQuestionsKey, question);
    },
    [onValue]
  );

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2}>
        <Box sx={styles.select}>
          <SelectWithValidate
            sx={{ width: '100%' }}
            name={questionTypeKey}
            options={quizQuestionOptions}
            optionsText={quizQuestionOptionsText}
            validation={questionTypeValidation}
            label={strings.polls.quizQuestionType}
            value={selectedQuestion.type}
            onValue={(key, value) => handleTypeValue(questionTypeKey, value, selectedQuestion)}
            onValidation={onValidation}
            hideValidationError={hideValidationError}
          />
        </Box>
        <TextInputWithValidate
          sx={styles.input}
          type="number"
          name={questionTimelimitKey}
          validation={questionTimelimitValidation}
          label={strings.polls.quizQuestionTimeLimitLabel}
          value={selectedQuestion.timelimit ?? 0}
          onValue={(key, value) => handleTextValue(questionTimelimitKey, value, selectedQuestion)}
          onValidation={onValidation}
          hideValidationError={hideValidationError}
          inputProps={{ min: 0 }}
          InputProps={{
            endAdornment: <InputAdornment position="end">sek</InputAdornment>,
          }}
        />
      </Stack>
      <TextInputWithValidate
        name={questionTextKey}
        validation={questionTitleValidation}
        label={strings.polls.quizQuestionLabel}
        value={selectedQuestion.text}
        onValue={(key, value) => handleTextValue(questionTextKey, value, selectedQuestion)}
        onValidation={onValidation}
        hideValidationError={hideValidationError}
      />
    </Stack>
  );
};

export default PollQuizQuestion;
